import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DefaultPageLayout } from "../components/layouts/DefaultPageLayout";
import { Button } from "../components/ui/button";
import { 
  FeatherArrowLeft,
  FeatherClock,
  FeatherCheckCircle,
  FeatherAlertTriangle,
  FeatherFile,
  FeatherLayout,
  FeatherCalendar,
  FeatherFileSmall,
  FeatherEdit2,
  FeatherSearch,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherArrowRight
} from "../components/ui/icons";
import { CheckSquare, XCircle, BookOpen, FileText, BarChart2 } from 'react-feather';
import { Avatar } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { ProjectBadge } from "../components/ui/project-badge";
import { IconWithBackground } from "../components/ui/IconWithBackground";
import { Tooltip } from "../components/ui/Tooltip";
import TaskTypeIcon from "../components/shared/TaskTypeIcon";
import TaskDueDate from "../components/shared/TaskDueDate";
import BunnyLoader from "../components/ui/BunnyLoader";
import { getMemberWorkPlanHistory } from "../services/managerService";
import TaskSummaryModal from "./components/TaskSummaryModal";
import "./WorkPlanHistory.css";

const WorkPlanHistory = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [workPlans, setWorkPlans] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [taskSummary, setTaskSummary] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [expandedSummaryTasks, setExpandedSummaryTasks] = useState({});
  const [collapsedWeeks, setCollapsedWeeks] = useState({});
  const [expandedTaskPullRequests, setExpandedTaskPullRequests] = useState({});
  const [expandedGeneralPullRequests, setExpandedGeneralPullRequests] = useState({});
  const [selectedTaskForModal, setSelectedTaskForModal] = useState(null);

  // Function to open task summary modal
  const openTaskSummaryModal = (taskId) => {
    const taskData = taskSummary[taskId];
    if (taskData) {
      setSelectedTaskForModal({
        id: taskId,
        ...taskData
      });
    }
  };

  // Function to close task summary modal
  const closeTaskSummaryModal = () => {
    setSelectedTaskForModal(null);
  };

  useEffect(() => {
    const fetchWorkPlanHistory = async () => {
      try {
        setLoading(true);
        const data = await getMemberWorkPlanHistory(userId);
        setUserData(data.user);
        setWorkPlans(data.workPlans || []);
        setHolidays(data.holidays || []);
        setApprovedLeaves(data.approvedLeaves || []);
        
        // Calculate task summary with project information
        const summary = {};
        data.workPlans.forEach(plan => {
          plan.tasks.forEach(task => {
            if (!summary[task.id]) {
              summary[task.id] = {
                title: task.title,
                description: task.description,
                totalHours: 0,
                occurrences: 0,
                project: task.project,
                feature_tag: task.feature_tag,
                task_type: task.task_type || 'Task',
                work_plan_attachments: []
              };
            }
            summary[task.id].totalHours += task.hours || 0;
            summary[task.id].occurrences += 1;
            
            // Collect attachments from all occurrences of this task
            if (task.work_plan_attachments && task.work_plan_attachments.length > 0) {
              summary[task.id].work_plan_attachments.push(...task.work_plan_attachments);
            }
          });
        });
        setTaskSummary(summary);
        
        setError(null);
      } catch (err) {
        setError("Failed to load work plan history");
        console.error("Error fetching work plan history:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkPlanHistory();
  }, [userId]);

  const getTaskHistory = (taskId) => {
    const history = [];
    workPlans.forEach(plan => {
      const task = plan.tasks.find(t => t.id === taskId);
      if (task) {
        const dateObj = new Date(plan.created_at);
        history.push({
          date: dateObj.toLocaleDateString('en-US', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          }),
          hours: task.hours || 0,
          notes: task.notes,
          status: plan.is_closed ? 'Completed' : 'In Progress'
        });
      }
    });
    return history;
  };

  // Add this function to get the most recent task status
  const getTaskStatus = (taskId) => {
    // Find the most recent work plan that contains this task
    for (const plan of workPlans) {
      const task = plan.tasks.find(t => t.id === taskId);
      if (task) {
        return task.status;
      }
    }
    return 'open'; // Default status if not found
  };

  // Add this function to get status variant for badge
  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'in-progress':
        return 'warning';
      case 'completed':
        return 'success';
      case 'open':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  // Add this function to calculate current week hours
  // Helper function to filter work plans by current year
  const getCurrentYearWorkPlans = () => {
    const currentYear = new Date().getFullYear();
    return workPlans.filter(plan => {
      const planDate = new Date(plan.created_at);
      return planDate.getFullYear() === currentYear;
    });
  };

  // Helper function to calculate total hours for work plans
  const calculateTotalHours = (plans) => {
    return plans.reduce((total, plan) => 
      total + plan.tasks.reduce((taskTotal, task) => 
        taskTotal + (task.hours || 0), 0), 0
    );
  };

  const getCurrentWeekHours = () => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setHours(0, 0, 0, 0);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // End of week (Saturday)

    return workPlans.reduce((total, plan) => {
      const planDate = new Date(plan.created_at);
      if (planDate >= startOfWeek && planDate <= endOfWeek) {
        return total + plan.tasks.reduce((taskTotal, task) => 
          taskTotal + (task.hours || 0), 0);
      }
      return total;
    }, 0);
  };

  // Add this function to calculate previous week hours
  const getPreviousWeekHours = () => {
    const now = new Date();
    const startOfCurrentWeek = new Date(now);
    startOfCurrentWeek.setHours(0, 0, 0, 0);
    startOfCurrentWeek.setDate(now.getDate() - now.getDay()); // Start of current week (Sunday)

    const startOfPreviousWeek = new Date(startOfCurrentWeek);
    startOfPreviousWeek.setDate(startOfCurrentWeek.getDate() - 7); // Start of previous week

    const endOfPreviousWeek = new Date(startOfPreviousWeek);
    endOfPreviousWeek.setDate(startOfPreviousWeek.getDate() + 6); // End of previous week

    return workPlans.reduce((total, plan) => {
      const planDate = new Date(plan.created_at);
      if (planDate >= startOfPreviousWeek && planDate <= endOfPreviousWeek) {
        return total + plan.tasks.reduce((taskTotal, task) => 
          taskTotal + (task.hours || 0), 0);
      }
      return total;
    }, 0);
  };

  // Calculate current month total hours
  const getCurrentMonthHours = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);
    const endOfMonth = new Date(now);

    return workPlans.reduce((total, plan) => {
      const planDate = new Date(plan.created_at);
      if (planDate >= startOfMonth && planDate <= endOfMonth) {
        return total + plan.tasks.reduce((taskTotal, task) => 
          taskTotal + (task.hours || 0), 0);
      }
      return total;
    }, 0);
  };

  // Calculate hours per project for current month
  const getProjectHoursThisMonth = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);
    const endOfMonth = new Date(now);

    const projectHours = {};

    workPlans.forEach(plan => {
      const planDate = new Date(plan.created_at);
      if (planDate >= startOfMonth && planDate <= endOfMonth) {
        plan.tasks.forEach(task => {
          if (task.project && task.project.name) {
            const projectName = task.project.name;
            if (!projectHours[projectName]) {
              projectHours[projectName] = 0;
            }
            projectHours[projectName] += task.hours || 0;
          }
        });
      }
    });

    return projectHours;
  };

  // Helper function to check if a date is a working day (Monday-Friday)
  const isWorkingDay = (date) => {
    const dayOfWeek = date.getDay();
    return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday = 1, Friday = 5
  };

  // Helper function to get week start date (Monday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  // Helper function to format week range
  const formatWeekRange = (weekStart) => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    const startMonth = weekStart.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = weekEnd.toLocaleDateString('en-US', { month: 'short' });
    const startDay = weekStart.getDate();
    const endDay = weekEnd.getDate();
    const year = weekStart.getFullYear();
    
    if (startMonth === endMonth) {
      return `${startMonth} ${startDay}-${endDay}, ${year}`;
    } else {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
    }
  };

  // Helper function to toggle week collapse
  const toggleWeekCollapse = (weekKey) => {
    setCollapsedWeeks(prev => ({
      ...prev,
      [weekKey]: !prev[weekKey]
    }));
  };

  // Helper function to collapse all weeks
  const collapseAllWeeks = () => {
    const weekKeys = generateTimeline().map(weekGroup => weekGroup.weekKey);
    const collapsedState = {};
    weekKeys.forEach(key => {
      collapsedState[key] = true;
    });
    setCollapsedWeeks(collapsedState);
  };

  // Helper function to expand all weeks
  const expandAllWeeks = () => {
    setCollapsedWeeks({});
  };

  // Helper function to check if all weeks are collapsed
  const areAllWeeksCollapsed = () => {
    const weekKeys = generateTimeline().map(weekGroup => weekGroup.weekKey);
    return weekKeys.every(key => collapsedWeeks[key]);
  };

  // Helper function to toggle task pull request expansion
  const toggleTaskPullRequestExpansion = (planTaskId) => {
    setExpandedTaskPullRequests(prev => ({
      ...prev,
      [planTaskId]: !prev[planTaskId]
    }));
  };

  // Helper function to toggle general pull request expansion
  const toggleGeneralPullRequestExpansion = (planId) => {
    setExpandedGeneralPullRequests(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  // Helper function to format date as YYYY-MM-DD for comparison
  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check if date is a weekend (Saturday or Sunday)
  const isWeekend = (date) => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
  };

  // Check if date is a company holiday
  const isHoliday = (date) => {
    if (!holidays || holidays.length === 0) return false;
    const dateString = formatDateString(date);
    const dateYear = date.getFullYear();
    
    return holidays.some(holiday => {
      if (!holiday.date) return false;
      if (holiday.year && holiday.year !== dateYear) return false;
      
      let holidayDateStr;
      if (typeof holiday.date === 'string') {
        holidayDateStr = holiday.date.split('T')[0].split(' ')[0];
      } else {
        // If it's a Date object, format it using UTC to avoid timezone shifts
        const holidayDate = new Date(holiday.date);
        const year = holidayDate.getUTCFullYear();
        const month = String(holidayDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(holidayDate.getUTCDate()).padStart(2, '0');
        holidayDateStr = `${year}-${month}-${day}`;
      }
      
      return holidayDateStr === dateString;
    });
  };

  // Check if date falls within an approved leave range
  const isApprovedLeave = (date) => {
    if (!approvedLeaves || approvedLeaves.length === 0) return false;
    const dateString = formatDateString(date);
    return approvedLeaves.some(leave => {
      return dateString >= leave.start_date && dateString <= leave.end_date;
    });
  };

  // Helper function to calculate leave statistics
  const getLeaveStats = () => {
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Create a set of all work plan dates
    const workPlanDates = new Set();
    workPlans.forEach(plan => {
      const planDate = new Date(plan.created_at);
      workPlanDates.add(formatDateString(planDate));
    });

    let approvedLeavesThisMonth = 0;
    let lopThisMonth = 0;
    let holidaysThisMonth = 0;
    let compOffThisMonth = 0;

    // Count stats for current month
    const currentDate = new Date(currentMonthStart);
    while (currentDate <= currentMonthEnd && currentDate <= now) {
      const dateString = formatDateString(currentDate);
      const isWeekendDay = isWeekend(currentDate);
      const isHolidayDay = isHoliday(currentDate);
      const isApprovedLeaveDay = isApprovedLeave(currentDate);
      const hasWorkPlan = workPlanDates.has(dateString);

      // Count holidays this month
      if (isHolidayDay) {
        holidaysThisMonth++;
      }

      // Count approved leaves this month (excluding weekends and holidays)
      if (isApprovedLeaveDay && !isWeekendDay && !isHolidayDay) {
        approvedLeavesThisMonth++;
      }

      // Count LOP this month (no work plan, not weekend, not holiday, not approved leave, and it's a working day)
      if (!hasWorkPlan && !isWeekendDay && !isHolidayDay && !isApprovedLeaveDay && isWorkingDay(currentDate)) {
        lopThisMonth++;
      }

      // Count Comp Off (work plans on weekends or holidays)
      if (hasWorkPlan && (isWeekendDay || isHolidayDay)) {
        compOffThisMonth++;
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return {
      approvedLeavesThisMonth,
      lopThisMonth,
      holidaysThisMonth,
      compOffThisMonth
    };
  };

  // Helper function to get monthly leave breakdown
  const getMonthlyLeaveBreakdown = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const months = [];
    
    // Create a set of all work plan dates
    const workPlanDates = new Set();
    workPlans.forEach(plan => {
      const planDate = new Date(plan.created_at);
      workPlanDates.add(formatDateString(planDate));
    });

    // Calculate stats for each month up to current month only
    for (let month = 0; month <= currentMonth; month++) {
      const monthStart = new Date(currentYear, month, 1);
      const monthEnd = new Date(currentYear, month + 1, 0); // Last day of month
      
      // Only count up to current date for current month
      const endDate = month === currentMonth ? now : monthEnd;
      
      let approvedLeaves = 0;
      let lop = 0;
      let holidays = 0;

      // Count stats for this month
      const currentDate = new Date(monthStart);
      while (currentDate <= endDate && currentDate <= now) {
        const dateString = formatDateString(currentDate);
        const isWeekendDay = isWeekend(currentDate);
        const isHolidayDay = isHoliday(currentDate);
        const isApprovedLeaveDay = isApprovedLeave(currentDate);
        const hasWorkPlan = workPlanDates.has(dateString);

        // Count holidays
        if (isHolidayDay) {
          holidays++;
        }

        // Count approved leaves (excluding weekends and holidays)
        if (isApprovedLeaveDay && !isWeekendDay && !isHolidayDay) {
          approvedLeaves++;
        }

        // Count LOP (no work plan, not weekend, not holiday, not approved leave, and it's a working day)
        if (!hasWorkPlan && !isWeekendDay && !isHolidayDay && !isApprovedLeaveDay && isWorkingDay(currentDate)) {
          lop++;
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Calculate ratio: (Approved Leaves + LOP) / Holidays
      // If holidays is 0, show the sum instead of division
      const ratio = holidays > 0 
        ? ((approvedLeaves + lop) / holidays).toFixed(2)
        : (approvedLeaves + lop);
      
      months.push({
        name: monthStart.toLocaleDateString('en-US', { month: 'short' }),
        ratio: ratio,
        approvedLeaves: approvedLeaves,
        lop: lop,
        holidays: holidays,
        isCurrentMonth: month === currentMonth
      });
    }

    return months;
  };

  // Helper function to generate timeline with work plans and leave cards
  const generateTimeline = () => {
    if (workPlans.length === 0) return [];

    // Sort work plans by date (newest first)
    const sortedPlans = [...workPlans].sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );

    const timeline = [];
    const planDates = new Set();

    // Add all work plans to timeline and track their dates
    sortedPlans.forEach(plan => {
      const planDate = new Date(plan.created_at);
      planDates.add(planDate.toDateString());
      timeline.push({
        type: 'work-plan',
        data: plan,
        date: planDate
      });
    });

    // Find missing working days between the first and last work plan
    if (sortedPlans.length > 1) {
      // Since we sorted newest first, the "first" element is actually the newest date
      // and the "last" element is the oldest date
      const newestDate = new Date(sortedPlans[0].created_at);
      const oldestDate = new Date(sortedPlans[sortedPlans.length - 1].created_at);
      
      const currentDate = new Date(oldestDate);
      currentDate.setDate(currentDate.getDate() + 1); // Start from day after oldest plan

      while (currentDate < newestDate) {
        const dateString = currentDate.toDateString();
        
        // Check if it's a working day and no work plan exists for this date
        if (isWorkingDay(currentDate) && !planDates.has(dateString)) {
          timeline.push({
            type: 'leave',
            date: new Date(currentDate),
            data: {
              id: `leave-${dateString}`,
              isLeave: true
            }
          });
        }
        
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    // Sort timeline by date (newest first)
    const sortedTimeline = timeline.sort((a, b) => b.date - a.date);

    // Group items by week
    const weekGroups = {};
    
    sortedTimeline.forEach(item => {
      const weekStart = getWeekStart(item.date);
      const weekKey = weekStart.toDateString();
      
      if (!weekGroups[weekKey]) {
        weekGroups[weekKey] = {
          weekStart,
          weekKey,
          weekRange: formatWeekRange(weekStart),
          items: []
        };
      }
      
      weekGroups[weekKey].items.push(item);
    });

    // Convert to array and sort by week (newest first)
    return Object.values(weekGroups).sort((a, b) => b.weekStart - a.weekStart);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <BunnyLoader 
          size="large" 
          text="Loading work plan history..."
          animation="bounce"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="history-container">
        <div className="header">
          <div className="header-left">
            <Button
              variant="neutral-tertiary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate(`/compact-work-plan-history/${userId}`)}
            >
              Back
            </Button>
            <div className="flex items-center gap-3">
              <h1 className="text-heading-2">Work Plan History</h1>
            </div>
          </div>
          <div className="header-right">
            <Button
              variant="neutral-tertiary"
              onClick={areAllWeeksCollapsed() ? expandAllWeeks : collapseAllWeeks}
            >
              {areAllWeeksCollapsed() ? 'Expand All' : 'Collapse All'}
            </Button>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherLayout />}
              onClick={() => navigate(`/work-plan-calendar/${userId}`)}
            >
              Calendar View
            </Button>
          </div>
        </div>

        {/* Add Member Details Section */}
        <div className="member-details-section">
          <div className="member-info-card">
            <div className="flex items-center gap-3">
              <div className="member-avatar">
                {userData?.display_name?.charAt(0) || 'U'}
              </div>
              <div className="member-details">
                <h2 className="text-heading-3">{userData?.display_name || 'Unknown Member'}</h2>
                <p className="text-body text-subtext-color">{userData?.email || 'No email available'}</p>
              </div>
            </div>
            <div className="member-stats">
              <div className="stats-year-label">
                <span className="year-text">{new Date().getFullYear()}</span>
                <span className="year-separator">|</span>
              </div>
              <div className="stats-content">
                {/* Count Stats - Current Year Only */}
                <div className="stats-group">
                  <div className="stat-item">
                    <span className="text-caption font-caption text-subtext-color">Total Plans</span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getCurrentYearWorkPlans().length}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="text-caption font-caption text-subtext-color">Completed Plans</span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getCurrentYearWorkPlans().filter(plan => plan.is_closed).length}
                    </span>
                  </div>
                </div>
                
                {/* Hours Stats - Grouped Together */}
                <div className="stats-group hours-group">
                  <div className="stat-item">
                    <span className="text-caption font-caption text-subtext-color">Total Hours</span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {calculateTotalHours(getCurrentYearWorkPlans())}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="text-caption font-caption text-subtext-color">This Month</span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getCurrentMonthHours()}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="text-caption font-caption text-subtext-color">This Week</span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getCurrentWeekHours()}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="text-caption font-caption text-subtext-color">Last Week</span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getPreviousWeekHours()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Project Hours This Month Row */}
            <div className="project-hours-row">
              <div className="project-hours-header">
                <span className="text-caption font-caption text-subtext-color">Project Hours This Month</span>
              </div>
              <div className="project-hours-list">
                {Object.keys(getProjectHoursThisMonth()).length === 0 ? (
                  <div className="project-hours-empty">
                    <span className="text-body font-body text-subtext-color">No project hours this month</span>
                  </div>
                ) : (
                  Object.entries(getProjectHoursThisMonth())
                    .sort((a, b) => b[1] - a[1]) // Sort by hours descending
                    .map(([projectName, hours]) => (
                      <div key={projectName} className="project-hours-item">
                        <span className="project-name">{projectName}</span>
                        <span className="project-hours-value">{hours} hrs</span>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Leave Statistics Section */}
        <div className="leave-stats-section">
          <div className="leave-stats-card">
            <div className="leave-stats-header">
              <div className="flex items-center gap-2">
                <IconWithBackground icon={<FeatherCalendar />} variant="neutral" />
                <h3 className="text-heading-4">Leave Tracking</h3>
              </div>
            </div>
            <div className="current-month-section">
              <h4 className="text-heading-5 current-month-title">Current Month</h4>
              <div className="leave-stats-grid">
                <div className="stat-item">
                  <span className="text-caption font-caption text-subtext-color">Approved</span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {getLeaveStats().approvedLeavesThisMonth}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="text-caption font-caption text-subtext-color">LOP</span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {getLeaveStats().lopThisMonth}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="text-caption font-caption text-subtext-color">Holidays</span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {getLeaveStats().holidaysThisMonth}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="text-caption font-caption text-subtext-color">Comp Off</span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {getLeaveStats().compOffThisMonth}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Monthly Breakdown */}
            <div className="monthly-breakdown">
              <div className="monthly-breakdown-header">
                <span className="text-caption font-caption text-subtext-color">Monthly Breakdown</span>
              </div>
              <div className="monthly-scale">
                {getMonthlyLeaveBreakdown().map((month, index) => {
                  const monthlyData = getMonthlyLeaveBreakdown();
                  // Find max ratio for scaling
                  const maxRatio = Math.max(...monthlyData.map(m => {
                    const ratioValue = typeof m.ratio === 'number' ? m.ratio : parseFloat(m.ratio);
                    return ratioValue || 0;
                  }));
                  const ratioValue = typeof month.ratio === 'number' ? month.ratio : parseFloat(month.ratio);
                  const scaleHeight = maxRatio > 0 ? (ratioValue / maxRatio) * 100 : 0;
                  
                  // Format display: show ratio if holidays > 0, otherwise show sum
                  const displayValue = month.holidays > 0 
                    ? `${month.approvedLeaves + month.lop}/${month.holidays}`
                    : `${month.approvedLeaves + month.lop}`;
                  
                  // Determine badge variant based on ratio
                  // If ratio <= 1, it's good (green), if > 1 and <= 2, warning (yellow), if > 2, critical (red)
                  let badgeVariant = 'success';
                  if (month.holidays > 0) {
                    if (ratioValue > 2) {
                      badgeVariant = 'error';
                    } else if (ratioValue > 1) {
                      badgeVariant = 'warning';
                    }
                  }
                  
                  return (
                    <div key={index} className="month-scale-item">
                      <div className="scale-bar-container">
                        <div 
                          className={`scale-bar ${month.isCurrentMonth ? 'current-month' : ''}`}
                          style={{ height: `${scaleHeight}%` }}
                        ></div>
                      </div>
                      <span className="month-label">{month.name}</span>
                      <div className="month-badges">
                        <Badge variant={badgeVariant} className="month-ratio-badge">
                          {displayValue}
                        </Badge>
                        <div className="month-details">
                          <span className="month-detail-item">
                            <Badge variant="neutral" className="detail-badge">A: {month.approvedLeaves}</Badge>
                          </span>
                          <span className="month-detail-item">
                            <Badge variant="neutral" className="detail-badge">L: {month.lop}</Badge>
                          </span>
                          {month.holidays > 0 && (
                            <span className="month-detail-item">
                              <Badge variant="neutral" className="detail-badge">H: {month.holidays}</Badge>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="work-plans-grid">
          {generateTimeline().map(weekGroup => {
            const isCollapsed = collapsedWeeks[weekGroup.weekKey];
            const itemCount = weekGroup.items.length;
            
            return (
              <div key={weekGroup.weekKey} className="week-group">
                <div 
                  className="week-separator collapsible"
                  onClick={() => toggleWeekCollapse(weekGroup.weekKey)}
                >
                  <div className="week-separator-content">
                    <div className="week-separator-line"></div>
                    <div className="week-separator-info">
                      <span className="week-separator-text">{weekGroup.weekRange}</span>
                      <span className="week-item-count">({itemCount} item{itemCount !== 1 ? 's' : ''})</span>
                    </div>
                    <div className="week-separator-line"></div>
                    <div className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>
                      ▼
                    </div>
                  </div>
                </div>
                
                {!isCollapsed && (
                  <div className="week-items">
                    {weekGroup.items.map(item => {
                      if (item.type === 'work-plan') {
                        const plan = item.data;
                        const planDate = new Date(plan.created_at);
                        // Reset time to start of day for accurate comparison
                        planDate.setHours(0, 0, 0, 0);
                        const isWeekendDay = isWeekend(planDate);
                        const isHolidayDay = isHoliday(planDate);
                        const isApprovedLeaveDay = isApprovedLeave(planDate);
                        const isCompOff = (isWeekendDay || isHolidayDay);
                        
                        return (
                          <div key={plan.id} className={`work-plan-card ${isCompOff ? 'comp-off-card' : ''}`}>
                            <div className="flex w-full items-center justify-between mb-4">
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                  <IconWithBackground icon={<FeatherClock />} />
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    {planDate.toLocaleDateString('en-US', {
                                      weekday: 'long',
                                      day: 'numeric',
                                      month: 'long',
                                      year: 'numeric'
                                    })}
                                  </span>
                                </div>
                                {/* Day Classification Badges */}
                                {(isWeekendDay || isHolidayDay || isApprovedLeaveDay || isCompOff) && (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {isWeekendDay && (
                                      <Badge variant="neutral" className="classification-badge">
                                        Week Off
                                      </Badge>
                                    )}
                                    {isHolidayDay && (
                                      <Badge variant="warning" className="classification-badge">
                                        Holiday
                                      </Badge>
                                    )}
                                    {isApprovedLeaveDay && (
                                      <Badge variant="neutral" className="classification-badge approved-leave-badge">
                                        On Approved Leave
                                      </Badge>
                                    )}
                                    {isCompOff && (
                                      <Badge variant="success" className="classification-badge comp-off-badge">
                                        Comp Off
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                              <Badge variant={plan.is_closed ? "success" : "warning"}>
                                {plan.is_closed ? "Completed" : "In Progress"}
                              </Badge>
                            </div>

                            <div className="flex w-full items-center justify-between rounded-md bg-neutral-50 px-6 py-4 mb-4">
                              <div className="flex flex-col items-start gap-1">
                                <span className="text-caption font-caption text-subtext-color">
                                  Tasks
                                </span>
                                <div className="flex items-center gap-2">
                                  <IconWithBackground
                                    icon={plan.task_count > 0 ? <FeatherCheckCircle /> : <FeatherAlertTriangle />}
                                  />
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    {plan.task_count}
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-col items-start gap-1">
                                <span className="text-caption font-caption text-subtext-color">
                                  Hours Logged
                                </span>
                                <div className="flex items-center gap-2">
                                  <IconWithBackground
                                    variant={plan.total_hours === 0 ? "warning" : "default"}
                                    icon={<FeatherClock />}
                                  />
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    {plan.total_hours}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="tasks-list">
                              {plan.tasks.map(task => {
                                const uniqueTaskId = `${plan.id}-${task.id}`;
                                
                                return (
                                  <div key={uniqueTaskId} className="task-item" >
                                    <div className="flex items-center gap-2">
                                      <TaskTypeIcon taskType={task.task_type} />
                                      <Tooltip 
                                  key={task.id} 
                                  title={task.title}
                                  description={task.description}
                                >
                                      <div className="flex items-center gap-2 grow">
                                        <span 
                                          className="text-body font-body text-default-font cursor-pointer hover:text-primary"
                                          onClick={() => setExpandedTasks(prev => ({
                                            ...prev,
                                            [uniqueTaskId]: !prev[uniqueTaskId]
                                          }))}
                                        >
                                          {task.display_id && (
                                            <span className="text-sm font-medium text-neutral-600 mr-2">
                                              [{task.display_id}]
                                            </span>
                                          )}
                                          {task.title}
                                        </span>
                                        {task.work_plan_attachments && task.work_plan_attachments.length > 0 && (
                                          <Tooltip content={`${task.work_plan_attachments.length} attachment${task.work_plan_attachments.length !== 1 ? 's' : ''}`}>
                                            <div className="attachment-indicator">
                                              <FeatherFileSmall size={8} />
                                            </div>
                                          </Tooltip>
                                        )}
                                        {task.task_pull_requests && task.task_pull_requests.length > 0 && (
                                          <Tooltip content={`${task.task_pull_requests.length} pull request${task.task_pull_requests.length !== 1 ? 's' : ''}`}>
                                            <div className="pull-request-indicator">
                                              <FeatherEdit2 size={6} />
                                            </div>
                                          </Tooltip>
                                        )}
                                        <ProjectBadge projectName={task.project?.name} />
                                        <TaskDueDate dueDate={task.feature_tag?.due_date} />
                                      </div>
                                      </Tooltip>
                                      <Badge variant="neutral">{task.hours || '0'} HOURS</Badge>
                                      <Tooltip content="View Task Summary">
                                        <button
                                          className="explore-task-button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openTaskSummaryModal(task.id);
                                          }}
                                          aria-label="View task summary"
                                        >
                                          <FeatherArrowRight size={16} />
                                        </button>
                                      </Tooltip>
                                    </div>
                                    
                                    {expandedTasks[uniqueTaskId] && (
                                      <div className="task-expanded-details">
                                        {task.notes && (
                                          <div className="task-notes mb-3">
                                            <h4 className="text-xs font-semibold text-gray-700 mb-2">Work Description</h4>
                                            <p className="text-xs text-gray-600 whitespace-pre-wrap">{task.notes}</p>
                                          </div>
                                        )}
                                        
                                        {/* Work Plan Attachments */}
                                        {task.work_plan_attachments && task.work_plan_attachments.length > 0 && (
                                          <div className="task-attachments">
                                            <h4 className="text-xs font-semibold text-gray-700 mb-2">Attachments</h4>
                                            <ul className="attachments-list text-sm">
                                              {task.work_plan_attachments.map(att => (
                                                <li key={att.id} className="attachment-item flex items-center gap-2 mb-2">
                                                  <a
                                                    href={att.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="attachment-link text-blue-700 hover:underline flex items-center gap-2"
                                                  >
                                                    <FeatherFileSmall size={26} />
                                                    <span>{att.file_name}</span>
                                                    <span className="file-size text-xs text-gray-500">({(att.file_size / 1024).toFixed(1)} KB)</span>
                                                  </a>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}

                                        {/* Task Pull Requests */}
                                        {task.task_pull_requests && task.task_pull_requests.length > 0 && (
                                          <div className="task-pull-requests" style={{ 
                                            backgroundColor: 'rgb(255 255 255)', 
                                            border:'0.5pt dashed #1b7003', 
                                            borderRadius: '7pt', 
                                            padding: '7.5pt', 
                                            marginTop: '7pt' 
                                          }}>
                                            <div 
                                              className="flex items-center justify-between cursor-pointer p-2 rounded transition-colors"
                                              style={{ 
                                                cursor: 'pointer',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                transition: 'background-color 0.2s'
                                              }}
                                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f8f8'}
                                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                toggleTaskPullRequestExpansion(`${plan.id}-${task.id}`);
                                              }}
                                            >
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs font-semibold text-gray-700">Pull Requests</span>
                                                <span className="text-xs text-gray-500">({task.task_pull_requests.length})</span>
                                              </div>
                                              <div className="flex items-center gap-1">
                                                <span className="text-xs text-gray-500">{expandedTaskPullRequests[`${plan.id}-${task.id}`] ? '' : ''}</span>
                                                <button className="text-gray-500 hover:text-gray-700 font-bold">
                                                  {expandedTaskPullRequests[`${plan.id}-${task.id}`] ? '−' : '+'}
                                                </button>
                                              </div>
                                            </div>
                                            {expandedTaskPullRequests[`${plan.id}-${task.id}`] && (
                                              <div className="space-y-2 mt-3">
                                                {task.task_pull_requests.map((pr, index) => (
                                                  <div key={pr.id} className="flex items-center gap-2">
                                                    <div className="pull-request-indicator">
                                                      <FeatherEdit2 size={14} />
                                                    </div>
                                                    <div className="flex-1">
                                                      <div className="flex items-center gap-2">
                                                        <a
                                                          href={pr.url}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="text-blue-700 hover:underline text-xs font-medium"
                                                        >
                                                          PR #{index + 1}
                                                        </a>
                                                        {pr.description && (
                                                          <span className="text-xs text-gray-600 break-words" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                                            - {pr.description}
                                                          </span>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* General Pull Requests */}
                            {plan.pull_requests && plan.pull_requests.length > 0 && (
                              <div className="general-pull-requests" style={{ 
                                backgroundColor: 'rgb(255 255 255)', 
                                border:'0.5pt dashed #1b7003', 
                                borderRadius: '7pt', 
                                padding: '7.5pt', 
                                marginTop: '7pt' 
                              }}>
                                <div 
                                  className="flex items-center justify-between cursor-pointer p-2 rounded transition-colors"
                                  style={{ 
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f8f8'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleGeneralPullRequestExpansion(plan.id);
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-gray-700">General Pull Requests</span>
                                    <span className="text-xs text-gray-500">({plan.pull_requests.length})</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-500">{expandedGeneralPullRequests[plan.id] ? '' : ''}</span>
                                    <button className="text-gray-500 hover:text-gray-700 font-bold">
                                      {expandedGeneralPullRequests[plan.id] ? '−' : '+'}
                                    </button>
                                  </div>
                                </div>
                                {expandedGeneralPullRequests[plan.id] && (
                                  <div className="space-y-2 mt-3">
                                    {plan.pull_requests.map((pr, index) => (
                                      <div key={pr.id} className="flex items-center gap-2">
                                        <div className="pull-request-indicator">
                                          <FeatherEdit2 size={14} />
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <a
                                              href={pr.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-700 hover:underline text-xs font-medium"
                                            >
                                              PR #{index + 1}
                                            </a>
                                            {pr.description && (
                                              <span className="text-xs text-gray-600 break-words" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                                - {pr.description}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      } else if (item.type === 'leave') {
                        const leaveDate = new Date(item.date);
                        leaveDate.setHours(0, 0, 0, 0);
                        const isWeekendDay = isWeekend(leaveDate);
                        const isHolidayDay = isHoliday(leaveDate);
                        const isApprovedLeaveDay = isApprovedLeave(leaveDate);
                        
                        // Determine the appropriate label and message
                        let leaveType = 'On Leave';
                        let mainMessage = 'No work plan created for this day';
                        let badgeVariant = 'neutral';
                        
                        if (isWeekendDay) {
                          leaveType = 'Week Off';
                          mainMessage = 'Weekend - No work plan required';
                          badgeVariant = 'neutral';
                        } else if (isHolidayDay) {
                          leaveType = 'Holiday';
                          mainMessage = 'Company holiday - No work plan required';
                          badgeVariant = 'warning';
                        } else if (isApprovedLeaveDay) {
                          leaveType = 'On Approved Leave';
                          mainMessage = 'Team member is on approved leave';
                          badgeVariant = 'neutral';
                        } else {
                          leaveType = 'LOP';
                          mainMessage = 'No work plan present - Loss of Pay';
                          badgeVariant = 'error';
                        }
                        
                        return (
                          <div key={item.data.id} className={`work-plan-card leave-card ${isHolidayDay ? 'holiday-card' : ''} ${isWeekendDay ? 'weekend-card' : ''}`}>
                            <div className="flex w-full items-center justify-between mb-4">
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                  <IconWithBackground 
                                    icon={<FeatherCalendar />} 
                                    variant="neutral"
                                  />
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    {leaveDate.toLocaleDateString('en-US', {
                                      weekday: 'long',
                                      day: 'numeric',
                                      month: 'long',
                                      year: 'numeric'
                                    })}
                                  </span>
                                </div>
                                {/* Day Classification Badges */}
                                {(isWeekendDay || isHolidayDay || isApprovedLeaveDay) && (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {isWeekendDay && (
                                      <Badge variant="neutral" className="classification-badge">
                                        Week Off
                                      </Badge>
                                    )}
                                    {isHolidayDay && (
                                      <Badge variant="warning" className="classification-badge">
                                        Holiday
                                      </Badge>
                                    )}
                                    {isApprovedLeaveDay && (
                                      <Badge variant="neutral" className="classification-badge approved-leave-badge">
                                        On Approved Leave
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                              <Badge variant={badgeVariant}>
                                {leaveType}
                              </Badge>
                            </div>

                            <div className="flex w-full items-center justify-center rounded-md bg-neutral-50 px-6 py-6">
                              <div className="text-center">
                                <p className="text-body font-body text-subtext-color">
                                  {mainMessage}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Task Summary Section */}
        <div className="task-summary-section">
          <div className="section-header">
            <div className="flex items-center gap-2">
              <IconWithBackground icon={<FeatherLayout />} />
              <h2 className="text-heading-3">Task Summary</h2>
            </div>
          </div>
          
          <div className="task-summary-grid">
            {Object.entries(taskSummary).map(([taskId, data]) => (
              <div key={taskId} id={`task-summary-${taskId}`} className="task-summary-card">
                <div className="flex items-center gap-2 mb-2">
                  <TaskTypeIcon taskType={data.task_type} />
                    <div className="flex items-center gap-2 grow">
                    <Tooltip 
                      key={taskId} 
                      title={data.title}
                      description={data.description}
                    >
                    <span 
                      className="text-body-bold font-body-bold text-default-font cursor-pointer hover:text-primary"
                      onClick={() => setExpandedSummaryTasks(prev => ({
                        ...prev,
                        [taskId]: !prev[taskId]
                      }))}
                    >
                      {data.title}
                    </span>
                    </Tooltip>
                    {data.work_plan_attachments && data.work_plan_attachments.length > 0 && (
                      <Tooltip content={`${data.work_plan_attachments.length} attachment${data.work_plan_attachments.length !== 1 ? 's' : ''}`}>
                        <div className="attachment-indicator">
                          <FeatherFileSmall size={8} />
                        </div>
                      </Tooltip>
                    )}
                    <ProjectBadge projectName={data.project?.name} />
                    <TaskDueDate dueDate={data.feature_tag?.due_date} />
                    <Badge variant={getStatusVariant(getTaskStatus(taskId))}>
                      {getTaskStatus(taskId)}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-caption font-caption text-subtext-color">
                      Total Hours
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {data.totalHours}
                    </span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-caption font-caption text-subtext-color">
                      Times Worked
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {data.occurrences}
                    </span>
                  </div>
                </div>

                {expandedSummaryTasks[taskId] && (
                  <div className="task-history-section">
                    <div className="task-history-header">
                      <span className="text-sm font-semibold">Task History</span>
                    </div>
                    <div className="task-history-list">
                      {getTaskHistory(taskId).map((entry, index) => (
                        <div key={index} className="task-history-item">
                          <div className="task-history-date">
                            <span className="text-sm font-medium">{entry.date}</span>
                            <Badge variant={entry.status === 'Completed' ? 'success' : 'warning'}>
                              {entry.status}
                            </Badge>
                          </div>
                          <div className="task-history-details">
                            <div className="task-history-hours">
                              <span className="text-sm text-subtext-color">Hours:</span>
                              <span className="text-sm font-medium">{entry.hours}</span>
                            </div>
                            {entry.notes && (
                              <div className="task-history-notes">
                                <span className="text-sm text-subtext-color">Notes:</span>
                                <span className="text-sm">{entry.notes}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Task Summary Modal */}
      <TaskSummaryModal
        task={selectedTaskForModal}
        isOpen={!!selectedTaskForModal}
        onClose={closeTaskSummaryModal}
        getTaskStatus={getTaskStatus}
        getStatusVariant={getStatusVariant}
        getTaskHistory={getTaskHistory}
      />
    </DefaultPageLayout>
  );
};

export default WorkPlanHistory; 